import { isDefined } from "@clipboard-health/util-ts";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
// eslint-disable-next-line no-restricted-imports
import { Box, IconButton, type SxProps, type Theme, Typography } from "@mui/material";
import { type CustomContentProps, useSnackbar, type VariantType } from "notistack";
import { forwardRef, type ReactNode } from "react";

/**
 * Soft pastel color palette for each toast variant.
 * Inspired by the Berlin design system tokens.
 */
const TOAST_STYLES: Record<
  VariantType,
  {
    background: string;
    borderColor: string;
    iconColor: string;
    textColor: string;
    icon: ReactNode;
  }
> = {
  success: {
    background: "#ECFDF3",
    borderColor: "#2FB690",
    iconColor: "#2FB690",
    textColor: "#067647",
    icon: <CheckCircleRoundedIcon fontSize="small" />,
  },
  error: {
    background: "#FEF3F2",
    borderColor: "#DD1717",
    iconColor: "#DD1717",
    textColor: "#B42318",
    icon: <ErrorRoundedIcon fontSize="small" />,
  },
  warning: {
    background: "#FFFAEB",
    borderColor: "#F79009",
    iconColor: "#F79009",
    textColor: "#B54708",
    icon: <WarningRoundedIcon fontSize="small" />,
  },
  info: {
    background: "#EFF8FF",
    borderColor: "#1890FF",
    iconColor: "#1890FF",
    textColor: "#175CD3",
    icon: <InfoRoundedIcon fontSize="small" />,
  },
  default: {
    background: "#EFF8FF",
    borderColor: "#1890FF",
    iconColor: "#1890FF",
    textColor: "#175CD3",
    icon: <InfoRoundedIcon fontSize="small" />,
  },
};

const toastContainerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  minWidth: {
    xs: "initial",
    md: 400,
  },
  maxWidth: {
    xs: "calc(100vw - 32px)",
    md: 560,
  },
  px: 2,
  py: 1.5,
  borderRadius: "12px",
  boxShadow: "0px 8px 24px -6px rgba(0, 0, 0, 0.12), 0px 2px 8px -2px rgba(0, 0, 0, 0.08)",
};

interface SnackbarToastProps extends CustomContentProps {
  onClick?: () => void;
  icon?: ReactNode;
}

export const SnackbarToast = forwardRef<HTMLDivElement, SnackbarToastProps>((props, ref) => {
  const { id, message, variant, style, icon, onClick } = props;
  const { closeSnackbar } = useSnackbar();
  const toastStyle = TOAST_STYLES[variant] ?? TOAST_STYLES.default;

  return (
    <Box
      ref={ref}
      id={String(id)}
      role="alert"
      style={style}
      sx={{
        ...toastContainerSx,
        backgroundColor: toastStyle.background,
        borderLeft: `4px solid ${toastStyle.borderColor}`,
        cursor: isDefined(onClick) ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: toastStyle.iconColor,
          flexShrink: 0,
        }}
      >
        {icon ?? toastStyle.icon}
      </Box>

      <Typography
        variant="body2"
        sx={{
          flex: 1,
          color: toastStyle.textColor,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        {message}
      </Typography>

      <IconButton
        aria-label="Close"
        size="small"
        sx={{
          flexShrink: 0,
          color: toastStyle.textColor,
          opacity: 0.5,
          p: 0.5,
          transition: "opacity 0.15s ease",
          "&:hover": {
            opacity: 1,
            backgroundColor: "transparent",
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
          closeSnackbar(id);
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
});

SnackbarToast.displayName = "SnackbarToast";
